import { MEDIA_FORMAT_MIME } from '@mg/shared';
import type {
  Asset,
  BlogExportFormat,
  DesignBundleTarget,
  MediaFormatFilter,
  MediaType,
} from '@mg/shared';
import { BLOG_EXPORT_FORMATS } from '@mg/shared';
import { MEDIA_GENERATOR_VENDOR_LABEL, MediaProvider } from '@mg/shared';
import { FileIcon, Film, Image as ImageIcon, Music, type LucideIcon } from 'lucide-react';
import type { BadgeTone } from '../../components/ui/Badge';
import { api } from '../../lib/api';

/**
 * Display labels for the BYO media generators — a narrowed view of the shared vendor map, which the
 * asset viewer also reads. Two copies of the same names would drift.
 */
export const mediaProviderLabel: Record<MediaProvider, string> = MEDIA_GENERATOR_VENDOR_LABEL;

/** Provider display order shared by the Settings card and the Generate modal. */
export const MEDIA_PROVIDERS: MediaProvider[] = [
  MediaProvider.OPENAI,
  MediaProvider.REPLICATE,
  MediaProvider.STABILITY,
  MediaProvider.FAL,
  MediaProvider.MAGNIFIC,
];

/**
 * The type filter chips offered in the toolbar (in display order).
 *
 * The last two are FORMATS, not media types, and they are named after where the files come from
 * because that is how people think of them: Figma exports SVG, Canva exports PPTX. They cannot be
 * folded into the type chips — an SVG already answers to "Images" (it carries the `image/` prefix)
 * and a PPTX to "other" — so each matches its exact MIME instead.
 */
export const MEDIA_TYPE_FILTERS: { id: MediaType | MediaFormatFilter | 'all'; label: string }[] = [
  { id: 'all', label: 'All types' },
  { id: 'image', label: 'Images' },
  { id: 'video', label: 'Video' },
  // No Audio chip: nothing in this product consumes an audio file — it cannot go in a post, a design
  // or a generation — so the filter only ever offered a way to look at an empty grid. `MediaType`
  // still knows about audio, and any that exists is still listed under All types.
  // Named by the FORMAT, not by the tool that happens to open it. Someone with an SVG may be taking
  // it into Illustrator, and a PPTX into PowerPoint — "Figma SVG" quietly told them otherwise.
  { id: 'svg', label: 'SVG' },
  { id: 'pptx', label: 'PPTX' },
];

/** The source filter chips offered in the toolbar (in display order). */
export const SOURCE_FILTERS: { id: 'all' | 'upload' | 'generation'; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'upload', label: 'Uploads' },
  { id: 'generation', label: 'Generations' },
];

/**
 * What KIND of file this is, named specifically.
 *
 * "Image" and "File" were true and useless: a library holding JPGs, PNGs, SVGs and PowerPoint decks
 * showed two words between them, and the distinction that matters most — an SVG or a PPTX cannot be
 * posted, a JPG or PNG can — was the one being hidden.
 *
 * SVG and PPTX are called a "file" rather than an image on purpose. Both carry a picture, and both
 * are editing formats this product generates for another tool to open; calling an SVG an image is
 * what leads someone to try attaching one to a post.
 */
const EXPLICIT_FILE_TYPE: Record<string, string> = {
  'image/svg+xml': 'SVG file',
  'image/jpeg': 'JPG image',
  'image/png': 'PNG image',
  'video/mp4': 'MP4 video',
  [MEDIA_FORMAT_MIME.pptx]: 'PPTX file',
};

/** The noun for anything not in the table above — a format we did not name, but can still describe. */
const MEDIA_TYPE_NOUN: Record<MediaType, string> = {
  image: 'image',
  video: 'video',
  audio: 'audio',
  other: 'file',
};

export function fileTypeLabel(asset: { mimeType: string; mediaType: MediaType }): string {
  // Parameters dropped first: `image/jpeg; charset=binary` is still a JPG.
  const mime = asset.mimeType.split(';')[0].trim().toLowerCase();
  const explicit = EXPLICIT_FILE_TYPE[mime];
  if (explicit) return explicit;

  // Anything else is described from its own subtype rather than falling back to "File", so a WEBP
  // reads as "WEBP image" instead of being lumped in with a PowerPoint deck.
  const subtype = mime.split('/')[1]?.split('+')[0];
  if (!subtype) return mediaTypeLabel[asset.mediaType];
  return `${subtype.toUpperCase()} ${MEDIA_TYPE_NOUN[asset.mediaType]}`;
}

export const mediaTypeLabel: Record<MediaType, string> = {
  image: 'Image',
  video: 'Video',
  audio: 'Audio',
  other: 'File',
};

export const mediaTypeIcon: Record<MediaType, LucideIcon> = {
  image: ImageIcon,
  video: Film,
  audio: Music,
  other: FileIcon,
};

/** Source badge label + tone: uploaded media reads neutral, AI-generated reads brand. */
export function sourceBadge(asset: Asset): { label: string; tone: BadgeTone } {
  return asset.source === 'generation'
    ? { label: 'AI', tone: 'brand' }
    : { label: 'Uploaded', tone: 'neutral' };
}

/** Human-readable byte size (B / KB / MB), e.g. 1.4 MB. `null` → em dash. */
export function formatBytes(bytes: number | null): string {
  if (bytes == null) return '—';
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb < 10 ? kb.toFixed(1) : Math.round(kb)} KB`;
  const mb = kb / 1024;
  return `${mb < 10 ? mb.toFixed(1) : Math.round(mb)} MB`;
}

/** A friendly display name for an asset (falls back to a type-based label). */
export function assetName(asset: Asset): string {
  return asset.name?.trim() || `Untitled ${mediaTypeLabel[asset.mediaType].toLowerCase()}`;
}

/** File extension for the download name, derived from the MIME subtype. */
function extensionFor(mimeType: string): string {
  const sub = mimeType.split('/')[1]?.split(';')[0]?.trim();
  if (!sub) return 'bin';
  if (sub === 'jpeg') return 'jpg';
  if (sub === 'mpeg') return 'mp3';
  if (sub === 'quicktime') return 'mov';
  return sub;
}

/** The filename used when downloading an asset (its name, or an id-based fallback). */
export function downloadFilename(asset: Asset): string {
  const name = asset.name?.trim();
  if (name) return /\.[a-z0-9]+$/i.test(name) ? name : `${name}.${extensionFor(asset.mimeType)}`;
  return `asset-${asset.id}.${extensionFor(asset.mimeType)}`;
}

/** Hand a fetched blob to the browser as a file download. */
function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * Fetch an asset's blob with the auth token attached and trigger a browser
 * download. Throws if the blob can't be fetched (caller surfaces the error).
 */
export async function downloadAsset(asset: Asset): Promise<void> {
  const blob = await api.fetchAssetBlob(asset.blobUrl);
  if (!blob) throw new Error('Could not download this file.');
  saveBlob(blob, downloadFilename(asset));
}

/**
 * Download a post's design bundle (artboards + copy + brand tokens) as one ZIP.
 * Resolves once the whole archive has arrived.
 */
export async function downloadDesignBundle(
  postId: string,
  target: DesignBundleTarget,
): Promise<void> {
  const { blob, filename } = await api.downloadDesignBundle(postId, target);
  saveBlob(blob, filename ?? 'design-bundle.zip');
}

/**
 * Download several assets as one ZIP, built and streamed by the API. Resolves once the whole
 * archive has arrived — a big selection takes a while, so callers keep a pending state up.
 */
/** Download a post as a file (Blog "Export" destination). Server builds it; we only save it. */
export async function downloadPostExport(
  postId: string,
  format: BlogExportFormat,
): Promise<void> {
  const { blob, filename } = await api.exportPost(postId, format);
  // Extension from the format table, not a ternary: adding a third format to that table must not
  // leave a download quietly saving an .html as post.md.
  const extension = BLOG_EXPORT_FORMATS.find((f) => f.value === format)?.extension ?? 'txt';
  saveBlob(blob, filename ?? `post.${extension}`);
}

export async function downloadAssetsZip(ids: string[]): Promise<void> {
  const { blob, filename } = await api.downloadAssetsZip(ids);
  saveBlob(blob, filename ?? 'media.zip');
}

/**
 * Download one stored asset by its auth-proxied URL.
 *
 * Goes through the proxy fetch rather than an `<a href>`: the blob sits behind the bearer guard, so a
 * plain link downloads the login page instead of the file.
 */
export async function downloadAssetByUrl(proxyUrl: string, filename: string): Promise<void> {
  const blob = await api.fetchAssetBlob(proxyUrl);
  if (!blob) throw new Error('That file is no longer available.');
  saveBlob(blob, filename);
}

/**
 * What an editing format is FOR, shown while its filter is active.
 *
 * Both are generated by this product for another tool to open, and neither can be posted. That second
 * half is the part worth saying out loud: an SVG and a PPTX both look like finished artwork in the
 * grid, and someone who tries to attach one to a post finds out only when the picker refuses it.
 */
export const FORMAT_FILTER_NOTICE: Partial<Record<MediaFormatFilter, { body: string; caution: string }>> = {
  svg: {
    body:
      'Smorgas Social uses SVG to generate editable designs for Figma or Adobe Illustrator. You can download generated SVG files and import them directly into your SVG editor of choice.',
    caution:
      'Note: SVG files are used exclusively by the Generate Design tool and are not compatible as final files to post on social media platforms. Please export final design as a JPG or PNG in order to post to social media.',
  },
  pptx: {
    body:
      'Smorgas Social uses PPTX to generate editable designs for Canva or PowerPoint. You can download generated PPTX files and import them directly into your PPTX editor of choice.',
    caution:
      'Note: PPTX files are used exclusively by the Generate Design tool and are not compatible as final files to post on social media platforms. Please export final design as a JPG or PNG in order to post to social media.',
  },
};
